import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../Common/AppOverlay";
import { postData } from "../../helpers/url-request";
import {
  broadcastLogout,
  clearAuthenticatedUser,
  getAuthenticatedUser,
} from "../../helpers/auth-session";
import { allowLogoutNavigation } from "../../helpers/unsaved-changes";
import {
  getLastUserActivity,
  hasPendingUserActivity,
  SESSION_IDLE_TIMEOUT_MS,
  SESSION_WARNING_MS,
  subscribeToUserActivity,
} from "../../helpers/session-activity";

const heartbeatIntervalMs = 60 * 1000;

const SessionTimeoutManager = () => {
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const lastHeartbeat = useRef(Date.now());
  const endingSession = useRef(false);

  const expireSession = useCallback(() => {
    if (endingSession.current || !getAuthenticatedUser()) return;
    endingSession.current = true;
    clearAuthenticatedUser();
    allowLogoutNavigation();
    broadcastLogout("expired");
    window.location.replace("/signin?sessionExpired=true");
  }, []);

  const keepAlive = useCallback(async () => {
    const response = await postData("/Authenticate/keepalive", {});
    if (!response?.status)
      throw new Error(response?.message || "Unable to extend the session.");
    lastHeartbeat.current = Date.now();
    setSecondsRemaining(null);
  }, []);

  useEffect(() => {
    const onActivity = () => setSecondsRemaining(null);
    const unsubscribe = subscribeToUserActivity(onActivity);

    const onServerExpiry = () => expireSession();
    window.addEventListener("piers:session-expired", onServerExpiry);

    const timer = window.setInterval(() => {
      if (!getAuthenticatedUser() || endingSession.current) return;
      const idleFor = Date.now() - getLastUserActivity();
      const remaining = SESSION_IDLE_TIMEOUT_MS - idleFor;

      if (remaining <= 0) {
        expireSession();
        return;
      }

      setSecondsRemaining(
        remaining <= SESSION_WARNING_MS ? Math.ceil(remaining / 1000) : null
      );

      if (
        hasPendingUserActivity() &&
        Date.now() - lastHeartbeat.current >= heartbeatIntervalMs
      ) {
        keepAlive().catch(() => undefined);
      }
    }, 1000);

    return () => {
      unsubscribe();
      window.removeEventListener("piers:session-expired", onServerExpiry);
      window.clearInterval(timer);
    };
  }, [expireSession, keepAlive]);

  const minutes =
    secondsRemaining == null ? 0 : Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining == null ? 0 : secondsRemaining % 60;

  return (
    <Modal
      isOpen={secondsRemaining != null}
      backdrop="static"
      keyboard={false}
      centered
    >
      <ModalHeader>Your session is about to expire</ModalHeader>
      <ModalBody>
        You will be signed out after 30 minutes of inactivity. Your session
        expires in{" "}
        <strong>
          {minutes}:{String(seconds).padStart(2, "0")}
        </strong>
        .
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={() => window.location.assign("/logout")}>
          Sign out
        </Button>
        <Button
          color="primary"
          onClick={() => keepAlive().catch(() => expireSession())}
        >
          Stay signed in
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SessionTimeoutManager;
